import { z } from 'zod';

import { CodeLanguage, UiLanguage } from './languages.js';
import { SourceCode, SourceLanguage, Workspace } from './project.js';

/**
 * AI tutor modes (plan §4.8). `solution` exists in the design but is disabled in the MVP: the
 * server refuses it.
 */
export const TutorMode = z.enum(['exercise', 'block', 'code', 'error', 'hint', 'question']);
export type TutorMode = z.infer<typeof TutorMode>;

export const TutorRequest = z.object({
  mode: TutorMode,
  uiLang: UiLanguage,
  codeLang: CodeLanguage,
  exerciseSlug: z.string().nullable(),
  projectId: z.string().nullable(),
  /**
   * The blocks as they are now. The server re-derives the program itself and never trusts code
   * it is sent: in code mode it parses `source` the same way (ADR 0012).
   */
  workspace: Workspace,
  source: SourceCode.nullable().optional(),
  sourceLang: SourceLanguage.nullable().optional(),
  selectedBlockId: z.string().max(100).nullable(),
  /** The line the student's cursor is on, used instead of the block id in code mode. */
  selectedLine: z.number().int().positive().nullable().optional(),
  /** The runtime error as the student saw it (already in the UI language). */
  error: z
    .object({ message: z.string().max(300), blockId: z.string().max(100).nullable() })
    .nullable(),
  hintLevel: z.number().int().min(1).max(3).nullable(),
  question: z.string().trim().max(500).nullable(),
});
export type TutorRequest = z.infer<typeof TutorRequest>;

export const TutorResponse = z.object({ id: z.string(), text: z.string() });
export type TutorResponse = z.infer<typeof TutorResponse>;

export const TutorFeedbackRequest = z.object({ id: z.string(), helpful: z.boolean() });
export type TutorFeedbackRequest = z.infer<typeof TutorFeedbackRequest>;
